import { Fragment, useMemo, useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { CalendarDay } from 'react-native-ui-datepicker';
import { Text } from '@ui-kitten/components';
import dayjs from 'dayjs';
import Tooltip from 'rn-tooltip';

import constants from '../../Config/constants';
import themeColor from '../../Theme/colors';
import themeStyle from '../../Theme/styles';

const PopoverMsg = () => (
  <Text appearance="alternative" style={themeStyle.textCenter}>
    No available seats for the number of party size
  </Text>
);

const Day = ({
  // from date picker
  date,
  isDisabled,
  isSelected,
  isToday,
  text,
  // custom props
  available,
  soldOutDates,
}: Props) => {
  const tooltipRef = useRef<Tooltip>();
  const dateDBformat = dayjs(date).format(constants.DBDATE_FORMAT);
  const isDaySoldOut = soldOutDates.includes(dateDBformat);
  const Wrapper = isDaySoldOut ? Tooltip : Fragment;
  const Container = isDaySoldOut ? Pressable : View;

  const appearance = useMemo(() => {
    if (isDisabled || isDaySoldOut) {
      return 'hint';
    } else if (isSelected) {
      return 'alternative';
    } else {
      return 'default';
    }
  }, [isDaySoldOut, isDisabled, isSelected]);

  const color = useMemo(() => {
    if (isDaySoldOut) {
      return { backgroundColor: themeColor.darkGray };
    } else if (isSelected) {
      return { backgroundColor: themeColor.white };
    }
    return { backgroundColor: themeColor.violet };
  }, [isDaySoldOut, isSelected]);

  const conditionalProps = useMemo(() => {
    if (isDaySoldOut) {
      return {
        wrapper: {
          ref: tooltipRef,
          actionType: 'none' as const,
          width: 210,
          height: 'auto',
          popover: <PopoverMsg />,
          withOverlay: false,
          backgroundColor: themeColor.darkGray,
        },
        container: {
          style: styles.soldOut,
          onHoverIn: () => tooltipRef.current?.toggleTooltip?.(),
          onHoverOut: () => tooltipRef.current?.toggleTooltip?.(),
        },
      };
    } else {
      return { container: { style: themeStyle.textCenter } };
    }
  }, [isDaySoldOut]);

  const availability = useMemo(() => {
    if (!available || available > 10) {
      if (isToday) {
        return (
          <View style={themeStyle.alignItemsCenter}>
            <View
              style={[
                styles.todayDotContainer,
                isDaySoldOut && styles.todayDotSoldOut,
              ]}
            >
              <View style={[styles.todayDot, color]} />
            </View>
          </View>
        );
      }
      return null;
    }
    return (
      <Text appearance="hint" category="c2" style={styles.availableText}>
        {available}
      </Text>
    );
  }, [available, color, isToday, isDaySoldOut]);

  return (
    <View
      style={[
        StyleSheet.absoluteFillObject,
        !isDaySoldOut && themeStyle.flexDirectionRowCenterCenter,
      ]}
    >
      {/* Wrapper = Fragment or Tooltip */}
      <Wrapper {...conditionalProps?.wrapper}>
        {/* Container = View or Pressable  */}
        <Container {...conditionalProps?.container}>
          {/* Display Text */}
          <Text appearance={appearance} category="p2">
            {text}
          </Text>
          {/* Availability States */}
          {availability}
        </Container>
      </Wrapper>
    </View>
  );
};

type Props = CalendarDay & {
  available: number;
  soldOutDates: string[];
};

const styles = StyleSheet.create({
  availableText: {
    color: '#ff1e1e',
  },
  todayDotContainer: {
    position: 'absolute',
  },
  todayDotSoldOut: {
    top: 10,
    left: -8,
  },
  todayDot: {
    borderRadius: 5,
    height: 5,
    width: 5,
  },
  soldOut: {
    ...themeStyle.flexDirectionRowCenterCenter,
    borderRadius: 5,
    position: 'absolute',
    left: -1,
    right: -1,
    top: -1,
    bottom: -43,
  },
});

export default Day;
